import { format } from "date-fns";

import { getTimeRangeFilter, TIME_RANGE_LABELS, TimeRange } from "../../../utils/timerange";
import { TeamBestPractices } from "../types";
import { formatUserNameFromEmail } from "./formatUserName";

export interface BestPracticeExample {
  score: number;
  agent_name?: string;
  agent_email: string;
  interaction_id: string;
  summary: string;
  details?: any;
  score_justification: string;
}

export interface AttributeBestPractices {
  attribute_name: string;
  examples: BestPracticeExample[];
}

export interface TeamBestPracticesData {
  lens_name: string;
  analysis_period: string;
  total_conversations: number;
  purpose: string;
  attributes: AttributeBestPractices[];
}

interface BuildTeamBestPracticesDataParams {
  bestPractices: TeamBestPractices;
  timeRange: TimeRange;
  timezone: string;
}

const formatAnalysisPeriod = (timeRange: TimeRange, timezone: string): string => {
  const { startDate, endDate } = getTimeRangeFilter(timeRange, timezone);
  const start = new Date(startDate);
  const end = new Date(endDate);

  const formatDate = (date: Date) => format(date, "MMM d, yyyy");

  const label = TIME_RANGE_LABELS[timeRange] || "Current Period";

  if (timeRange === "allTime") {
    return `All Time (through ${formatDate(end)})`;
  }

  if (timeRange.startsWith("custom:")) {
    return `${formatDate(start)} - ${formatDate(end)}`;
  }

  return `${label} (${formatDate(start)} - ${formatDate(end)})`;
};

export const buildTeamBestPracticesData = ({
  bestPractices,
  timeRange,
  timezone
}: BuildTeamBestPracticesDataParams): TeamBestPracticesData => {
  const analysisPeriod = formatAnalysisPeriod(timeRange, timezone);

  const purpose = "This document showcases the highest-performing examples across the entire company for each attribute, demonstrating what exceptional execution looks like within the company today and providing a proven opportunity, a benchmark, and a learning resource for all team members.";

  const attributeNames = Object.keys(bestPractices.attributes).sort();

  const attributes: AttributeBestPractices[] = attributeNames.map(attributeName => {
    const examples = bestPractices.attributes[attributeName] || [];

    return {
      attribute_name: attributeName,
      examples: examples.map(example => ({
        score: example.score_value,
        agent_name: formatUserNameFromEmail(example.user_email),
        agent_email: example.user_email,
        interaction_id: example.interaction_id,
        summary: example.summary,
        details: example.details,
        score_justification: example.score_justification
      }))
    };
  }).filter(attr => attr.examples.length > 0);

  const data: TeamBestPracticesData = {
    lens_name: bestPractices.lens_name,
    analysis_period: analysisPeriod,
    total_conversations: bestPractices.total_conversations,
    purpose,
    attributes
  };

  return data;
};
